// Home / Command Center queries.
//
// Schema after migration 0009: deals + pipeline_stages were dropped. Open
// pipeline is now expressed as projects.status IN ('quote', 'active').
// Project invoices live in project_invoices (renamed from deal_invoices).
// Stuck detection uses the projects_with_stuck view.

import { useQuery } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';

const LEAD_24H_BUCKETS = 6;
const HOT_LEAD_SCORE = 80;
const HOT_LEAD_SLA_MS = 24 * 60 * 60 * 1000;

export interface StuckProjectRow {
  id: string;
  name: string;
  customer_id: string | null;
  customer_name: string | null;
  days_in_status: number;
  value_amount: number;
  value_currency: string;
}

export interface HotLeadRow {
  id: string;
  full_name: string;
  company_name: string | null;
  score: number | null;
  status: string;
  received_at: string;
}

export interface ProjectAtRiskRow {
  id: string;
  name: string;
  status: string;
  reason: 'stuck' | 'paused';
}

export interface OverdueTaskRow {
  id: string;
  title: string;
  due_date: string;
  project_id: string | null;
  project_name: string | null;
}

export interface CommandCenterDecisions {
  stuckProjects: StuckProjectRow[];
  hotLeadsAwaiting: HotLeadRow[];
  overdueTasks: OverdueTaskRow[];
}

export interface CommandCenterKpis {
  monthIncome: number;
  monthIncomeCurrency: string;
  pipelineOpenValue: number;
  pipelineOpenCount: number;
  weeklyLeads: number;
  weeklyHotLeads: number;
  todayTasks: number;
  overdueTasks: number;
  leads24hSparkline: number[];
  avgDaysToClose: number | null;
}

export interface ActivityRow {
  id: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  created_at: string;
  user_id: string | null;
}

function todayIsoDate(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function startOfMonthIso(): string {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function startOfWeekIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

async function loadCustomerNames(orgId: string, ids: string[]): Promise<Record<string, string>> {
  if (ids.length === 0) return {};
  const { data } = await supabase
    .from('companies')
    .select('id, name')
    .eq('organization_id', orgId)
    .in('id', ids);
  if (!data) return {};
  return data.reduce<Record<string, string>>((acc, c) => {
    acc[c.id] = c.name;
    return acc;
  }, {});
}

// ---- KPIs ------------------------------------------------------------------

async function fetchKpis(orgId: string): Promise<CommandCenterKpis> {
  const yesterdayIso = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const weekAgoIso = startOfWeekIso();
  const monthStartIso = startOfMonthIso();
  const todayIso = todayIsoDate();

  const [invoicesRes, projectsRes, leadsWeekRes, leadsRecentRes, tasksTodayRes, tasksOverdueRes, closedProjectsRes] =
    await Promise.all([
      supabase
        .from('project_invoices')
        .select('amount, currency, paid_at, status')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .eq('status', 'paid')
        .gte('updated_at', monthStartIso),
      supabase
        .from('projects')
        .select('value_amount, status')
        .eq('organization_id', orgId)
        .is('deleted_at', null),
      supabase
        .from('leads')
        .select('id, score, score_class')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .gt('received_at', weekAgoIso),
      supabase
        .from('leads')
        .select('received_at')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .gt('received_at', yesterdayIso)
        .order('received_at', { ascending: true }),
      supabase
        .from('tasks')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .eq('due_date', todayIso)
        .not('status', 'in', '("done","cancelled")'),
      supabase
        .from('tasks')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .lt('due_date', todayIso)
        .not('status', 'in', '("done","cancelled")'),
      supabase
        .from('projects')
        .select('started_at, completed_at')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .eq('status', 'completed')
        .gt('completed_at', new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString()),
    ]);

  if (leadsRecentRes.error) throw leadsRecentRes.error;
  if (leadsWeekRes.error) throw leadsWeekRes.error;
  if (tasksTodayRes.error) throw tasksTodayRes.error;
  if (tasksOverdueRes.error) throw tasksOverdueRes.error;

  let monthIncome = 0;
  let monthIncomeCurrency = 'ILS';
  for (const row of (invoicesRes.data ?? []) as { amount: number; currency: string }[]) {
    monthIncome += row.amount;
    if (row.currency) monthIncomeCurrency = row.currency;
  }

  const projects = (projectsRes.data ?? []) as { value_amount: number; status: string }[];
  let pipelineOpenValue = 0;
  let pipelineOpenCount = 0;
  for (const row of projects) {
    if (row.status === 'quote' || row.status === 'active') {
      pipelineOpenValue += row.value_amount;
      pipelineOpenCount += 1;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const leadsWeek = (leadsWeekRes.data ?? []) as { score_class: string }[];
  const weeklyLeads = leadsWeek.length;
  const weeklyHotLeads = leadsWeek.filter((l) => l.score_class === 'hot').length;

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const arrivals = (leadsRecentRes.data ?? []) as { received_at: string }[];
  const bucketStart = Date.now() - 24 * 60 * 60 * 1000;
  const bucketWidth = (24 * 60 * 60 * 1000) / LEAD_24H_BUCKETS;
  const leads24hSparkline = Array.from({ length: LEAD_24H_BUCKETS }, () => 0);
  arrivals.forEach((row) => {
    const t = new Date(row.received_at).getTime();
    const idx = Math.min(LEAD_24H_BUCKETS - 1, Math.max(0, Math.floor((t - bucketStart) / bucketWidth)));
    const current = leads24hSparkline[idx] ?? 0;
    leads24hSparkline[idx] = current + 1;
  });

  let avgDaysToClose: number | null = null;
  const closedRows = (closedProjectsRes.data ?? []) as { started_at: string | null; completed_at: string | null }[];
  const closedWithDates = closedRows.filter(
    (r): r is { started_at: string; completed_at: string } => Boolean(r.started_at) && Boolean(r.completed_at),
  );
  if (closedWithDates.length > 0) {
    const totalDays = closedWithDates.reduce((sum, r) => {
      const diffMs = new Date(r.completed_at).getTime() - new Date(r.started_at).getTime();
      const days = Math.max(0, diffMs / (24 * 60 * 60 * 1000));
      return sum + days;
    }, 0);
    avgDaysToClose = Math.round(totalDays / closedWithDates.length);
  }

  return {
    monthIncome,
    monthIncomeCurrency,
    pipelineOpenValue,
    pipelineOpenCount,
    weeklyLeads,
    weeklyHotLeads,
    todayTasks: tasksTodayRes.count ?? 0,
    overdueTasks: tasksOverdueRes.count ?? 0,
    leads24hSparkline,
    avgDaysToClose,
  };
}

// ---- Decisions Queue -------------------------------------------------------

async function fetchDecisions(orgId: string): Promise<CommandCenterDecisions> {
  const todayIso = todayIsoDate();
  const hotSlaCutoffIso = new Date(Date.now() - HOT_LEAD_SLA_MS).toISOString();

  const [projectsRes, hotLeadsRes, overdueTasksRes] = await Promise.all([
    supabase
      .from('projects_with_stuck')
      .select('id, name, company_id, value_amount, value_currency, status, is_stuck, created_at')
      .eq('organization_id', orgId)
      .is('deleted_at', null)
      .eq('is_stuck', true),
    supabase
      .from('leads')
      .select('id, full_name, company_name, score, status, received_at')
      .eq('organization_id', orgId)
      .is('deleted_at', null)
      .gte('score', HOT_LEAD_SCORE)
      .in('status', ['new', 'contacted'])
      .lt('received_at', hotSlaCutoffIso)
      .order('received_at', { ascending: false })
      .limit(10),
    supabase
      .from('tasks')
      .select('id, title, due_date, project_id')
      .eq('organization_id', orgId)
      .is('deleted_at', null)
      .lt('due_date', todayIso)
      .not('status', 'in', '("done","cancelled")')
      .order('due_date', { ascending: true })
      .limit(10),
  ]);

  const stuckProjectRows = (projectsRes.data ?? []) as {
    id: string;
    name: string;
    company_id: string | null;
    value_amount: number;
    value_currency: string;
    status: string;
    is_stuck: boolean | null;
    created_at: string;
  }[];

  const customerIds = Array.from(
    new Set(stuckProjectRows.map((p) => p.company_id).filter((id): id is string => id !== null)),
  );
  const customerById = await loadCustomerNames(orgId, customerIds);

  const now = Date.now();
  const stuckProjects: StuckProjectRow[] = stuckProjectRows.map((p) => {
    const days = Math.max(
      0,
      Math.floor((now - new Date(p.created_at).getTime()) / (24 * 60 * 60 * 1000)),
    );
    return {
      id: p.id,
      name: p.name,
      customer_id: p.company_id,
      customer_name: p.company_id ? customerById[p.company_id] ?? null : null,
      days_in_status: days,
      value_amount: p.value_amount,
      value_currency: p.value_currency,
    };
  });

  const hotLeadsAwaiting = (hotLeadsRes.data ?? []) as HotLeadRow[];

  // Hydrate overdue tasks with project names.
  const overdueRaw = (overdueTasksRes.data ?? []) as {
    id: string;
    title: string;
    due_date: string;
    project_id: string | null;
  }[];
  const projectIds = Array.from(new Set(overdueRaw.map((t) => t.project_id).filter((id): id is string => id !== null)));
  let projectNameById: Record<string, string> = {};
  if (projectIds.length > 0) {
    const { data: pRows } = await supabase
      .from('projects')
      .select('id, name')
      .eq('organization_id', orgId)
      .in('id', projectIds);
    projectNameById = (pRows ?? []).reduce<Record<string, string>>((acc, p) => {
      acc[p.id] = p.name;
      return acc;
    }, {});
  }
  const overdueTasks: OverdueTaskRow[] = overdueRaw.map((t) => ({
    id: t.id,
    title: t.title,
    due_date: t.due_date,
    project_id: t.project_id,
    project_name: t.project_id ? (projectNameById[t.project_id] ?? null) : null,
  }));

  return { stuckProjects, hotLeadsAwaiting, overdueTasks };
}

async function fetchHotLeads(orgId: string): Promise<HotLeadRow[]> {
  const { data, error } = await supabase
    .from('leads')
    .select('id, full_name, company_name, score, status, received_at')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .gte('score', HOT_LEAD_SCORE)
    .in('status', ['new', 'contacted'])
    .order('received_at', { ascending: false })
    .limit(5);
  if (error) throw error;
  return data;
}

async function fetchProjectsAtRisk(orgId: string): Promise<ProjectAtRiskRow[]> {
  const { data, error } = await supabase
    .from('projects_with_stuck')
    .select('id, name, status, is_stuck')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .in('status', ['active', 'paused']);
  if (error) throw error;

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const rows = (data ?? []) as { id: string; name: string; status: string; is_stuck: boolean | null }[];
  const atRisk: ProjectAtRiskRow[] = [];
  for (const p of rows) {
    if (p.status === 'paused') {
      atRisk.push({ id: p.id, name: p.name, status: p.status, reason: 'paused' });
    } else if (p.is_stuck === true) {
      atRisk.push({ id: p.id, name: p.name, status: p.status, reason: 'stuck' });
    }
  }
  return atRisk.slice(0, 5);
}

async function fetchRecentActivity(orgId: string): Promise<ActivityRow[]> {
  const { data, error } = await supabase
    .from('audit_log')
    .select('id, action, entity_type, entity_id, created_at, user_id')
    .eq('organization_id', orgId)
    .order('created_at', { ascending: false })
    .limit(10);

  if (error) throw error;
  return data;
}

// ---- React Query hooks -----------------------------------------------------

export function useCommandCenterKpis(orgId: string | null) {
  return useQuery({
    queryKey: ['command-center-kpis', orgId] as const,
    queryFn: () => {
      if (!orgId) {
        return Promise.resolve<CommandCenterKpis>({
          monthIncome: 0,
          monthIncomeCurrency: 'ILS',
          pipelineOpenValue: 0,
          pipelineOpenCount: 0,
          weeklyLeads: 0,
          weeklyHotLeads: 0,
          todayTasks: 0,
          overdueTasks: 0,
          leads24hSparkline: Array.from({ length: LEAD_24H_BUCKETS }, () => 0),
          avgDaysToClose: null,
        });
      }
      return fetchKpis(orgId);
    },
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useCommandCenterDecisions(orgId: string | null) {
  return useQuery({
    queryKey: ['command-center-decisions', orgId] as const,
    queryFn: () => {
      if (!orgId) {
        return Promise.resolve<CommandCenterDecisions>({
          stuckProjects: [],
          hotLeadsAwaiting: [],
          overdueTasks: [],
        });
      }
      return fetchDecisions(orgId);
    },
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useHotLeads(orgId: string | null) {
  return useQuery({
    queryKey: ['command-center-hot-leads', orgId] as const,
    queryFn: () => {
      if (!orgId) return Promise.resolve<HotLeadRow[]>([]);
      return fetchHotLeads(orgId);
    },
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}

export function useProjectsAtRisk(orgId: string | null) {
  return useQuery({
    queryKey: ['command-center-projects-at-risk', orgId] as const,
    queryFn: () => {
      if (!orgId) return Promise.resolve<ProjectAtRiskRow[]>([]);
      return fetchProjectsAtRisk(orgId);
    },
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}

export function useRecentActivity(orgId: string | null) {
  return useQuery({
    queryKey: ['home-activity', orgId] as const,
    queryFn: () => {
      if (!orgId) return Promise.resolve<ActivityRow[]>([]);
      return fetchRecentActivity(orgId);
    },
    enabled: Boolean(orgId),
    staleTime: 30 * 1000,
  });
}
